import React, { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  imageProducts, generateImageBatch, imageSendApproval, imageWorkflow,
  createCampaignPack, campaignPackage, agencyCampaign, agencyCampaignStatus, imageIntel, getBrandKit, putBrandKit,
  pollImageJob, getCategoryProfile, addPromptPreset, deletePromptPreset, putBrandProfile, improveVariation,
} from "@/lib/api";
import { ImproveCompareDialog } from "@/components/ImproveCompareDialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Sparkle, PaperPlaneTilt, DownloadSimple, Lightning, ShieldCheck, LockKey, PaintBrush,
  Star, ArrowsClockwise, Rocket, CheckCircle, Megaphone, CalendarBlank, TrendUp, Package, Confetti,
  Warning, X, BookmarkSimple, QrCode, CaretDown, CaretUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PRESETS = [
  { v: "instagram_feed", l: "Instagram Feed" }, { v: "instagram_story", l: "Instagram Story" },
  { v: "facebook_feed", l: "Facebook Feed" }, { v: "facebook_story", l: "Facebook Story" },
  { v: "pinterest", l: "Pinterest Pin" }, { v: "google_display", l: "Google Display" },
  { v: "google_shopping", l: "Google Shopping" }, { v: "email_banner", l: "Email Banner" },
  { v: "website_hero", l: "Website Hero" }, { v: "shopify_product", l: "Shopify Product" },
];
const COMPOSITIONS = [{ v: "lifestyle", l: "Lifestyle" }, { v: "product_only", l: "Product Only" }, { v: "person", l: "With Person" }, { v: "seasonal", l: "Seasonal" }];
const QUALITIES = [{ v: "low", l: "Draft" }, { v: "medium", l: "Standard" }, { v: "high", l: "Ultra" }];
const CAMERAS = [{ v: "hero", l: "Hero Product" }, { v: "lifestyle", l: "Lifestyle" }, { v: "closeup", l: "Close-up Detail" }];
const SEASONS = [
  { v: "", l: "No season" }, { v: "spring", l: "Spring" }, { v: "summer", l: "Summer" },
  { v: "fall", l: "Fall" }, { v: "winter", l: "Winter" }, { v: "christmas", l: "Christmas" },
  { v: "halloween", l: "Halloween" }, { v: "thanksgiving", l: "Thanksgiving" },
  { v: "valentines", l: "Valentine's Day" }, { v: "fathers_day", l: "Father's Day" },
  { v: "mothers_day", l: "Mother's Day" }, { v: "memorial_day", l: "Memorial Day" },
  { v: "fourth_july", l: "Fourth of July" }, { v: "labor_day", l: "Labor Day" },
  { v: "back_to_school", l: "Back to School" },
];
const SCORE_ROWS = [
  ["Brand", "brand_accuracy"], ["Logo", "logo_placement"], ["Market", "marketing_effectiveness"],
  ["Appeal", "visual_appeal"], ["Convert", "conversion_potential"],
];
const CAMPAIGN_ACTIONS = [
  { a: "facebook_ad", l: "Facebook Campaign" }, { a: "instagram_post", l: "Instagram Campaign" },
  { a: "pinterest", l: "Pinterest Pin" }, { a: "google_ad", l: "Google Display Ad" },
  { a: "email_campaign", l: "Email Campaign" }, { a: "blog_header", l: "Blog Header" },
  { a: "video_concept", l: "Video / Reel Concept" },
];

export function ScoreBadge({ score }) {
  const [open, setOpen] = useState(false);
  if (!score) return null;
  const c = score.overall >= 90 ? "text-emerald-400" : score.overall >= 80 ? "text-amber-400" : "text-red-400";
  const reasons = score.reasons || [];
  const suggestions = score.suggestions || [];
  return (
    <div className="rounded-lg bg-white/[0.03] border border-white/8 px-2.5 py-2 mt-2" data-testid="image-score">
      <div className="flex items-center justify-between">
        <span className="text-[10px] label-caps text-[#7a7a7a]">{score.ai_scored ? "AI Vision Score" : "AI Quality Score"}</span>
        <span className={`text-lg font-head font-bold ${c}`}>{score.overall}</span>
      </div>
      <div className="grid grid-cols-5 gap-1 mt-1">
        {SCORE_ROWS.map(([k, key]) => (
          <div key={k} className="text-center">
            <div className="text-[8px] text-[#7a7a7a] uppercase">{k}</div>
            <div className="text-[11px] text-[#cfcfcf] font-semibold">{score[key] ?? "—"}</div>
          </div>
        ))}
      </div>
      {(reasons.length > 0 || suggestions.length > 0) && (
        <button data-testid="score-why-toggle" onClick={() => setOpen((o) => !o)}
          className="mt-1.5 flex items-center gap-1 text-[10px] text-[#9a9a9a] hover:text-white">
          {open ? <CaretUp size={10} /> : <CaretDown size={10} />} Why this score
        </button>
      )}
      {open && (
        <div className="mt-1 space-y-1" data-testid="score-details">
          {reasons.map((r, i) => <div key={`r${i}`} className="text-[10px] text-[#9a9a9a] leading-snug">• {r}</div>)}
          {suggestions.map((s, i) => <div key={`s${i}`} className="text-[10px] text-amber-300/80 leading-snug">💡 {s}</div>)}
        </div>
      )}
    </div>
  );
}

export function StudioV2({ clientId, client }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState("brand_lock");
  const [prompt, setPrompt] = useState("");
  const [preset, setPreset] = useState("instagram_feed");
  const [composition, setComposition] = useState("lifestyle");
  const [quality, setQuality] = useState("medium");
  const [camera, setCamera] = useState("hero");
  const [season, setSeason] = useState("");
  const [bundle, setBundle] = useState(false);
  const [selected, setSelected] = useState([]);
  const [variations, setVariations] = useState([]);
  const [favorite, setFavorite] = useState(null);
  const [picked, setPicked] = useState([]);
  const [compare, setCompare] = useState(false);
  const [busy, setBusy] = useState(false);
  const [regenLabel, setRegenLabel] = useState(null);
  const [progress, setProgress] = useState({ done: 0, total: 4, current: "" });
  const [failedConcepts, setFailedConcepts] = useState([]);
  const [improving, setImproving] = useState(null);
  const [comparePass, setComparePass] = useState(null);

  const intelQ = useQuery({ queryKey: ["image-intel", clientId], queryFn: () => imageIntel(clientId), enabled: !!clientId });
  const productsQ = useQuery({ queryKey: ["image-products", clientId], queryFn: () => imageProducts(clientId), enabled: !!clientId });
  const kitQ = useQuery({ queryKey: ["brand-kit", clientId], queryFn: () => getBrandKit(clientId), enabled: !!clientId });
  const profileQ = useQuery({ queryKey: ["category-profile", clientId], queryFn: () => getCategoryProfile(clientId), enabled: !!clientId });
  const intel = intelQ.data || {};
  const products = productsQ.data || [];
  const kit = kitQ.data || {};
  const catProfile = profileQ.data || {};
  const compositions = catProfile.compositions || COMPOSITIONS;
  const presets = catProfile.presets || [];
  const customPresetLabels = new Set((kit.custom_presets || []).map((p) => p.label));
  const assets = kit?.assets || {};
  const hasProduct = !!(assets.packaging || assets.mug);
  const hasLogo = !!assets.logo;
  const canBundle = !!(assets.packaging && assets.mug);
  const productWord = assets.packaging ? "packaging" : assets.mug ? "mug" : "product";
  const isProductBiz = ["coffee", "ecommerce"].includes(catProfile.category);

  useEffect(() => { setVariations([]); setFavorite(null); setPicked([]); setSelected([]); setFailedConcepts([]); }, [clientId]);
  useEffect(() => {
    if (compositions.length && !compositions.some((c) => c.v === composition)) setComposition(compositions[0].v);
  }, [clientId, profileQ.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleProduct = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const togglePick = (p) => setPicked((s) => (s.includes(p) ? s.filter((x) => x !== p) : [...s, p]));

  const mergeVariations = (vars, replaceAll) => {
    if (!vars.length) return;
    if (replaceAll) setVariations(vars);
    else setVariations((prev) => {
      const map = Object.fromEntries(prev.map((v) => [v.concept, v]));
      vars.forEach((v) => { map[v.concept] = v; });
      return Object.values(map);
    });
  };

  const runBatch = async (concepts = null) => {
    if (mode === "brand_lock" && isProductBiz && !hasProduct) {
      toast.warning("Brand Lock places your real product into each scene — upload your official packaging or mug in the Brand Kit / Create tab, or switch to Creative Mode.");
    }
    if (mode === "brand_lock" && !isProductBiz && !hasLogo) {
      toast.warning("Brand Lock overlays your REAL logo on every image — upload your official logo in the Create tab → Brand Assets for exact branding.");
    }
    setBusy(true);
    if (concepts) setRegenLabel(concepts[0]); else { setVariations([]); setFavorite(null); setPicked([]); setFailedConcepts([]); }
    setProgress({ done: 0, total: concepts ? concepts.length : 4, current: "" });
    try {
      // Async job: returns instantly, then we poll — no long request, no Cloudflare timeout.
      const { job_id } = await generateImageBatch({ client_id: clientId, prompt, preset, product_ids: selected, composition, quality, mode, camera, season: season || null, bundle, concepts });
      const job = await pollImageJob(job_id, (j) => {
        setProgress({ done: j.done_count || 0, total: j.total || 4, current: j.current || "" });
        mergeVariations(j.variations || [], !concepts);
      });
      if (job.status === "error") {
        if (concepts) setFailedConcepts((prev) => [...prev.filter((f) => !concepts.includes(f.concept)), ...(job.failed || [])]);
        toast.error(job.error || "Generation failed — please try again");
      } else {
        mergeVariations(job.variations || [], !concepts);
        const failed = job.failed || [];
        if (concepts) {
          const okConcepts = new Set((job.variations || []).map((v) => v.concept));
          setFailedConcepts((prev) => [
            ...prev.filter((f) => !okConcepts.has(f.concept) && !concepts.includes(f.concept)),
            ...failed,
          ]);
        } else {
          setFailedConcepts(failed);
        }
        qc.invalidateQueries({ queryKey: ["library", clientId] });
        if (failed.length) toast.warning(`${failed.length} variation(s) failed (${failed.map((f) => f.concept).join(", ")}) — hit Retry on the card`);
        else toast.success(mode === "brand_lock" ? "Brand-locked variations ready" : "Creative variations ready");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed — please try again");
    }
    setBusy(false); setRegenLabel(null);
  };

  const runImprove = async (v) => {
    setImproving(v.concept);
    try {
      const { job_id } = await improveVariation({
        client_id: clientId, concept: v.concept, original_image_path: v.image_path,
        original_score: v.score || null, prompt, preset, composition, quality, mode, camera,
        season: season || null, bundle, product_ids: selected,
      });
      const job = await pollImageJob(job_id);
      if (job.status === "error") toast.error(job.error || "Improve pass failed — try again");
      else {
        setComparePass(job.result);
        qc.invalidateQueries({ queryKey: ["library", clientId] });
      }
    } catch { toast.error("Improve pass failed — try again"); }
    setImproving(null);
  };

  const keepImproved = () => {
    if (!comparePass) return;
    const imp = comparePass.improved;
    setVariations((prev) => prev.map((v) => v.concept === comparePass.concept
      ? { ...v, image_path: imp.image_path, score: imp.score, logo_applied: imp.logo_applied, product_locked: imp.product_locked }
      : v));
    if (favorite === comparePass.original?.image_path) setFavorite(imp.image_path);
    setPicked((p) => p.map((x) => (x === comparePass.original?.image_path ? imp.image_path : x)));
    toast.success(`"${comparePass.concept}" upgraded to the improved design`);
    setComparePass(null);
  };

  const savePresetNow = async () => {
    if (!prompt.trim()) return toast.error("Write a prompt first, then save it as a preset");
    const label = window.prompt("Preset name (e.g. Summer Lawn Care)");
    if (!label) return;
    try {
      await addPromptPreset({ client_id: clientId, label, prompt });
      qc.invalidateQueries({ queryKey: ["category-profile", clientId] });
      qc.invalidateQueries({ queryKey: ["brand-kit", clientId] });
      toast.success(`Preset "${label}" saved for this brand`);
    } catch { toast.error("Could not save preset"); }
  };

  const removePreset = async (label) => {
    try {
      await deletePromptPreset({ client_id: clientId, label, prompt: "" });
      qc.invalidateQueries({ queryKey: ["category-profile", clientId] });
      qc.invalidateQueries({ queryKey: ["brand-kit", clientId] });
    } catch { toast.error("Could not delete preset"); }
  };

  const sendApprovalMut = useMutation({
    mutationFn: async () => {
      const items = picked.length ? picked : favorite ? [favorite] : variations.map((v) => v.image_path);
      for (const p of items) {
        await imageSendApproval({ client_id: clientId, image_path: p, title: `${client?.name || "Brand"} — ${(prompt || preset).slice(0, 50)}` });
      }
      return items.length;
    },
    onSuccess: (n) => { toast.success(`${n} image(s) sent to Approval Queue — nothing publishes until you approve`); setPicked([]); },
    onError: () => toast.error("Failed to send to approval"),
  });

  const workflowMut = useMutation({
    mutationFn: ({ image_path, action }) => imageWorkflow({ client_id: clientId, image_path, action, product_title: prompt || client?.name || "" }),
    onSuccess: () => toast.success("Queued to Approval Queue — approval-first, nothing auto-publishes"),
    onError: () => toast.error("Failed to queue campaign asset"),
  });

  const [smartBusy, setSmartBusy] = useState(false);
  const runSmartCampaign = async () => {
    setSmartBusy(true);
    toast.loading("Building Smart Campaign… runs in the background (~1-2 min)", { id: "smartpack" });
    try {
      const { job_id } = await createCampaignPack({ client_id: clientId, theme: prompt || `${intel?.month || ""} campaign`, preset, product_ids: selected });
      const job = await pollImageJob(job_id, (j) => {
        if (j.current) toast.loading(`${j.current}… (${j.done_count || 0}/${j.total || 2})`, { id: "smartpack" });
      });
      if (job.status === "done") toast.success(`Smart Campaign queued — ${job.result?.created} items in the Approval Queue`, { id: "smartpack" });
      else toast.error(job.error || "Campaign generation failed", { id: "smartpack" });
    } catch { toast.error("Campaign generation failed", { id: "smartpack" }); }
    setSmartBusy(false);
  };

  const [pkgBusy, setPkgBusy] = useState(false);
  const runPackage = async () => {
    setPkgBusy(true);
    toast.loading("Rendering campaign placements… runs in the background (~2-3 min)", { id: "pkg" });
    try {
      const { job_id } = await campaignPackage({ client_id: clientId, prompt, camera, season: season || null, product_ids: selected, bundle, quality });
      const job = await pollImageJob(job_id, (j) => {
        if (j.current) toast.loading(`${j.current}… (${j.done_count || 0}/${j.total || 3} scenes)`, { id: "pkg" });
      });
      if (job.status === "done") toast.success(`Campaign Package queued — ${job.result?.created} placements in the Approval Queue (one consistent style)`, { id: "pkg" });
      else toast.error(job.error || "Campaign package generation failed", { id: "pkg" });
    } catch { toast.error("Campaign package generation failed", { id: "pkg" }); }
    setPkgBusy(false);
  };

  const monthName = intel?.month || new Date().toLocaleString("en-US", { month: "long" });
  const [agencyBusy, setAgencyBusy] = useState(false);
  const runAgency = async () => {
    setAgencyBusy(true);
    toast.loading(`Building your ${monthName} campaign… this runs in the background (~1-2 min)`, { id: "agency" });
    try {
      const { job_id } = await agencyCampaign({ client_id: clientId, month: monthName, theme: prompt, season: season || null, quality });
      let tries = 0;
      const poll = async () => {
        try {
          const job = await agencyCampaignStatus(job_id);
          if (job.status === "done") {
            const r = job.result || {};
            toast.success(`${r.theme} built — ${r.created} assets across ${r.channels?.length || 0} channels queued to Approval (score ${r.score?.overall})`, { id: "agency" });
            setAgencyBusy(false);
            return;
          }
          if (job.status === "error" || tries > 60) {
            toast.error(job.error ? `Campaign failed: ${job.error}` : "Campaign timed out", { id: "agency" });
            setAgencyBusy(false);
            return;
          }
          tries += 1;
          setTimeout(poll, 3000);
        } catch {
          toast.error("Could not check campaign status", { id: "agency" });
          setAgencyBusy(false);
        }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      toast.error("Could not start campaign", { id: "agency" });
      setAgencyBusy(false);
    }
  };

  const selectedNames = products.filter((p) => selected.includes(p.shopify_id)).map((p) => p.title);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5" data-testid="studio-v2">
      {/* CONTROLS */}
      <div className="lg:col-span-2 space-y-4">
        {/* One-Click full agency campaign */}
        <div className="card-matte p-4 border border-[#6B3DFF]/30" data-testid="agency-campaign-card">
          <div className="flex items-center gap-2 mb-1">
            <Rocket size={18} className="brand-text" weight="fill" />
            <span className="font-head font-semibold text-sm">One-Click Agency Campaign</span>
          </div>
          <p className="text-[11px] text-[#9a9a9a] mb-2.5">Auto-selects products & bundles, then builds images + copy for every channel → Approval Queue. Nothing publishes automatically.</p>
          <Button data-testid="create-month-campaign-btn" onClick={runAgency} disabled={agencyBusy}
            className="w-full bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">
            <Rocket size={16} className={`mr-1.5 ${agencyBusy ? "ai-thinking" : ""}`} weight="fill" />
            {agencyBusy ? "Building full campaign…" : `Create ${monthName} Campaign`}
          </Button>
        </div>
        {/* Mode toggle */}
        <div className="card-matte p-4">
          <div className="flex items-center justify-between">
            <Label className="label-caps text-[#7a7a7a]">Generation Mode</Label>
            {catProfile.label && (
              <span data-testid="brand-category-chip" className="text-[10px] px-2 py-0.5 rounded-full border border-[#6B3DFF]/30 text-[#cfcfcf]">
                {catProfile.emoji} {catProfile.label}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button data-testid="mode-brand-lock" onClick={() => setMode("brand_lock")}
              className={`flex flex-col items-start gap-1 rounded-lg border px-3 py-2.5 text-left transition-colors ${mode === "brand_lock" ? "bg-[#6B3DFF]/15 border-[#6B3DFF]" : "border-white/10 hover:border-white/25"}`}>
              <span className="flex items-center gap-1.5 text-sm font-semibold text-white"><LockKey size={15} weight="fill" className="brand-text" /> Brand Lock</span>
              <span className="text-[10px] text-[#9a9a9a] leading-tight">Products & branding stay exact. AI only changes the scene.</span>
            </button>
            <button data-testid="mode-creative" onClick={() => setMode("creative")}
              className={`flex flex-col items-start gap-1 rounded-lg border px-3 py-2.5 text-left transition-colors ${mode === "creative" ? "bg-[#6B3DFF]/15 border-[#6B3DFF]" : "border-white/10 hover:border-white/25"}`}>
              <span className="flex items-center gap-1.5 text-sm font-semibold text-white"><PaintBrush size={15} weight="fill" className="brand-text" /> Creative</span>
              <span className="text-[10px] text-[#9a9a9a] leading-tight">New concepts, holiday editions & experiments.</span>
            </button>
          </div>
          <div className={`mt-2 flex items-start gap-1.5 text-[11px] rounded-lg px-2.5 py-1.5 border ${mode === "brand_lock" ? ((isProductBiz ? hasProduct : hasLogo) ? "bg-emerald-500/10 border-emerald-500/25" : "bg-amber-500/10 border-amber-500/25") : "bg-white/[0.03] border-white/10"}`}>
            {mode === "brand_lock"
              ? (isProductBiz
                ? (hasProduct
                  ? <><ShieldCheck size={13} className="text-emerald-400 mt-0.5 shrink-0" weight="fill" /><span className="text-[#cfcfcf]">Your real official {productWord} is placed into each generated scene — artwork preserved exactly, no redrawn branding.</span></>
                  : <><ShieldCheck size={13} className="text-amber-400 mt-0.5 shrink-0" weight="fill" /><span className="text-[#cfcfcf]">No official packaging/mug found — upload it in the Brand Kit / Create tab so Brand Lock can place your real product.</span></>)
                : (hasLogo
                  ? <><ShieldCheck size={13} className="text-emerald-400 mt-0.5 shrink-0" weight="fill" /><span className="text-[#cfcfcf]">Your REAL logo is overlaid pixel-exact on every image — the AI never redraws it. It only invents the scene, layout and background.</span></>
                  : <><ShieldCheck size={13} className="text-amber-400 mt-0.5 shrink-0" weight="fill" /><span className="text-[#cfcfcf]">Upload your official logo (Create tab → Brand Assets) so Brand Lock can apply your exact branding to every image.</span></>))
              : <><PaintBrush size={13} className="brand-text mt-0.5 shrink-0" /><span className="text-[#cfcfcf]">Creative Mode — the AI may design new concepts. Use intentionally.</span></>}
          </div>
        </div>

        {/* Smart prompt */}
        <div className="card-matte p-4 space-y-3">
          <div>
            <div className="flex items-center justify-between">
              <Label className="label-caps">Describe your campaign (Smart Prompt)</Label>
              <button data-testid="save-preset-btn" onClick={savePresetNow}
                className="flex items-center gap-1 text-[10px] text-[#9a9a9a] hover:text-white">
                <BookmarkSimple size={12} /> Save as preset
              </button>
            </div>
            <Textarea data-testid="v2-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2}
              className="bg-[#10101A] border-white/10 mt-1" placeholder='e.g. "Create a Summer cold-brew campaign"' />
          </div>
          {presets.length > 0 && (
            <div data-testid="preset-row">
              <div className="flex items-center gap-1.5 mb-1.5"><BookmarkSimple size={13} className="brand-text" weight="fill" /><span className="text-[10px] label-caps text-[#7a7a7a]">Prompt Presets · {catProfile.label}</span></div>
              <div className="flex flex-wrap gap-1.5">
                {presets.map((p, i) => (
                  <span key={`${p.label}-${i}`} className="flex items-center gap-0.5 rounded-lg border border-white/8 hover:border-[#6B3DFF]/40">
                    <button data-testid={`preset-${i}`} onClick={() => setPrompt(p.prompt)}
                      className="text-[11px] pl-2 pr-1 py-1 text-[#9a9a9a] hover:text-white">{p.label}</button>
                    {customPresetLabels.has(p.label) && (
                      <button data-testid={`preset-del-${i}`} onClick={() => removePreset(p.label)}
                        className="pr-1.5 text-[#555] hover:text-red-400"><X size={10} /></button>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}
          {(intel.suggested_prompts || []).length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-1.5"><TrendUp size={13} className="brand-text" /><span className="text-[10px] label-caps text-[#7a7a7a]">AI Marketing Intelligence · {intel.season} {intel.month}</span></div>
              <div className="flex flex-wrap gap-1.5">
                {intel.suggested_prompts.map((s, i) => (
                  <button key={i} data-testid={`smart-suggest-${i}`} onClick={() => setPrompt(s)}
                    className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg border border-white/8 text-[#9a9a9a] hover:text-white hover:border-[#6B3DFF]/40">
                    <Lightning size={11} className="brand-text" /> {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="label-caps">Ad Preset</Label>
              <Select value={preset} onValueChange={setPreset}>
                <SelectTrigger className="bg-[#10101A] border-white/10 mt-1" data-testid="v2-preset"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#14141F] border-white/10 text-white">{PRESETS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="label-caps">Scene</Label>
              <Select value={composition} onValueChange={setComposition}>
                <SelectTrigger className="bg-[#10101A] border-white/10 mt-1" data-testid="v2-composition"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#14141F] border-white/10 text-white">{compositions.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="label-caps">Quality</Label>
              <Select value={quality} onValueChange={setQuality}>
                <SelectTrigger className="bg-[#10101A] border-white/10 mt-1" data-testid="v2-quality"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#14141F] border-white/10 text-white">{QUALITIES.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div>
            <Label className="label-caps">Camera Mode</Label>
            <div className="grid grid-cols-3 gap-1 mt-1">
              {CAMERAS.map((cm) => (
                <button key={cm.v} data-testid={`v2-camera-${cm.v}`} onClick={() => setCamera(cm.v)}
                  className={`text-[11px] py-1.5 rounded-md border ${camera === cm.v ? "bg-[#6B3DFF] text-white border-[#6B3DFF]" : "border-white/10 text-[#9a9a9a] hover:border-white/25"}`}>{cm.l}</button>
              ))}
            </div>
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1"><Label className="label-caps">Season / Holiday</Label>
              <Select value={season || "none"} onValueChange={(v) => setSeason(v === "none" ? "" : v)}>
                <SelectTrigger className="bg-[#10101A] border-white/10 mt-1" data-testid="v2-season"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#14141F] border-white/10 text-white max-h-64">
                  {SEASONS.map((s) => <SelectItem key={s.v || "none"} value={s.v || "none"}>{s.l}</SelectItem>)}
                </SelectContent>
              </Select></div>
            {canBundle && (
              <button data-testid="v2-bundle-toggle" onClick={() => setBundle((b) => !b)}
                className={`flex items-center gap-1 text-xs px-3 py-2 rounded-lg border whitespace-nowrap ${bundle ? "bg-[#6B3DFF] text-white border-[#6B3DFF]" : "border-white/15 text-[#9a9a9a] hover:border-white/30"}`}>
                <Package size={13} /> Bundle (bag + mug)
              </button>
            )}
          </div>
          <Button data-testid="v2-generate-btn" onClick={() => runBatch()} disabled={busy}
            className="w-full bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">
            <Sparkle size={16} className={`mr-1.5 ${busy && !regenLabel ? "ai-thinking" : ""}`} weight="fill" />
            {busy && !regenLabel
              ? (progress.current
                ? `Variation ${Math.min(progress.done + 1, progress.total)} of ${progress.total} — ${progress.current}…`
                : "Starting generation…")
              : "Generate 4 Variations"}
          </Button>
          <Button data-testid="v2-campaign-btn" onClick={runSmartCampaign} disabled={smartBusy}
            variant="outline" className="w-full border-[#6B3DFF]/40 bg-transparent text-white hover:bg-[#6B3DFF]/10">
            <Rocket size={15} className={`mr-1.5 ${smartBusy ? "ai-thinking" : ""}`} weight="fill" />
            {smartBusy ? "Building campaign…" : "One-Click Smart Campaign → Approval"}
          </Button>
          <Button data-testid="v2-package-btn" onClick={runPackage} disabled={pkgBusy}
            variant="outline" className="w-full border-white/15 bg-transparent text-white hover:bg-white/5">
            <Megaphone size={15} className={`mr-1.5 ${pkgBusy ? "ai-thinking" : ""}`} weight="fill" />
            {pkgBusy ? "Rendering 10 placements…" : "Full Campaign Package (10 placements) → Approval"}
          </Button>
        </div>

        {/* Product selection */}
        <div className="card-matte p-4" data-testid="v2-product-picker">
          <div className="flex items-center gap-1.5 mb-1"><Package size={15} className="brand-text" weight="fill" /><span className="font-head font-semibold text-sm">Products {selected.length > 0 && <span className="brand-text">({selected.length})</span>}</span></div>
          <p className="text-[11px] text-[#7a7a7a] mb-2">{products.length ? "Select Shopify products to feature (Bundle Mode = pick 2+)." : "Connect & sync Shopify to feature real products."}</p>
          <div className="max-h-56 overflow-y-auto space-y-1 pr-1">
            {products.map((p) => {
              const on = selected.includes(p.shopify_id);
              return (
                <button key={p.shopify_id} data-testid={`v2-product-${p.shopify_id}`} onClick={() => toggleProduct(p.shopify_id)}
                  className={`w-full flex items-center gap-2 rounded-lg border px-2 py-1.5 text-left ${on ? "bg-[#6B3DFF]/12 border-[#6B3DFF]/50" : "border-white/8 hover:border-white/20"}`}>
                  <span className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${on ? "bg-[#6B3DFF] border-[#6B3DFF]" : "border-white/25"}`}>{on && <CheckCircle size={12} weight="fill" className="text-white" />}</span>
                  {p.image ? <img src={p.image} alt="" className="h-8 w-8 rounded object-cover bg-[#0a0a0a]" /> : <span className="h-8 w-8 rounded bg-[#0a0a0a]" />}
                  <span className="flex-1 min-w-0"><span className="block text-xs text-[#cfcfcf] truncate">{p.title}</span><span className="block text-[10px] text-[#7a7a7a]">${p.price} · {p.inventory ?? 0} in stock</span></span>
                </button>
              );
            })}
            {!products.length && <div className="text-xs text-[#7a7a7a] py-3 text-center">No synced products.</div>}
          </div>
        </div>
      </div>

      {/* VARIATIONS */}
      <div className="lg:col-span-3 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="font-head font-semibold">Variations</span>
            {variations.length > 0 && <span className="text-xs text-[#7a7a7a]">{selectedNames.length ? selectedNames.join(", ") : "Brand scene"}</span>}
          </div>
          {variations.length > 0 && (
            <div className="flex items-center gap-2">
              <button data-testid="v2-compare-toggle" onClick={() => setCompare((c) => !c)} className={`text-xs px-3 py-1.5 rounded-lg border ${compare ? "bg-[#6B3DFF] text-white border-[#6B3DFF]" : "border-white/15 text-[#9a9a9a]"}`}>Compare side-by-side</button>
              <Button data-testid="v2-regen-all" size="sm" variant="outline" onClick={() => runBatch()} disabled={busy} className="border-white/15 bg-transparent text-white hover:bg-white/5"><ArrowsClockwise size={14} className="mr-1" /> Regenerate all</Button>
              <Button data-testid="v2-send-selected" size="sm" onClick={() => sendApprovalMut.mutate()} disabled={sendApprovalMut.isPending} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white"><PaperPlaneTilt size={14} className="mr-1" /> Send {picked.length ? `${picked.length} ` : favorite ? "favorite " : "all "}to Approval</Button>
            </div>
          )}
        </div>

        {!variations.length && !busy ? (
          <div className="card-matte p-12 text-center text-[#7a7a7a]" data-testid="v2-empty">
            <Sparkle size={36} className="brand-text mx-auto mb-3" weight="fill" />
            Describe a campaign and hit <b className="text-[#cfcfcf]">Generate 4 Variations</b>. Each comes with an AI score and one-click campaign tools.
          </div>
        ) : (
          <div className={`grid gap-3 ${compare ? "grid-cols-4" : "grid-cols-1 sm:grid-cols-2"}`}>
            {variations.map((v, i) => {
              const isFav = favorite === v.image_path;
              const isPicked = picked.includes(v.image_path);
              const loadingThis = busy && regenLabel === v.concept;
              return (
                <div key={v.concept} className={`card-matte p-3 ${isFav ? "ring-2 ring-[#6B3DFF]" : ""}`} data-testid={`v2-variation-${i}`}>
                  <div className="relative rounded-lg overflow-hidden bg-[#0a0a0a] border border-white/8 mb-2">
                    {loadingThis
                      ? <div className="w-full aspect-square flex items-center justify-center"><Sparkle size={26} className="brand-text ai-thinking" weight="fill" /></div>
                      : <img src={`${BACKEND}${v.image_path}`} alt={v.concept} className="w-full object-cover" />}
                    <span className="absolute top-2 left-2 px-2 py-0.5 rounded text-[10px] bg-black/70 text-white">{v.concept}</span>
                    <button data-testid={`v2-fav-${i}`} onClick={() => setFavorite(isFav ? null : v.image_path)} title="Choose favorite"
                      className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/60 flex items-center justify-center">
                      <Star size={15} className={isFav ? "text-[#6B3DFF]" : "text-white"} weight={isFav ? "fill" : "regular"} />
                    </button>
                    <label data-testid={`v2-pick-${i}`} className="absolute bottom-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded bg-black/60 cursor-pointer text-[10px] text-white">
                      <input type="checkbox" checked={isPicked} onChange={() => togglePick(v.image_path)} className="accent-[#6B3DFF]" /> Select
                    </label>
                  </div>
                  {!compare && <ScoreBadge score={v.score} />}
                  {!compare && (
                    <Button data-testid={`v2-improve-${i}`} size="sm" onClick={() => runImprove(v)}
                      disabled={busy || !!improving}
                      className="w-full mt-2 bg-white/[0.05] hover:bg-[#6B3DFF]/20 border border-[#6B3DFF]/30 text-[#cfcfcf]">
                      <Sparkle size={13} className={`mr-1.5 brand-text ${improving === v.concept ? "ai-thinking" : ""}`} weight="fill" />
                      {improving === v.concept ? "Creative Director's second pass…" : "✨ Improve This Design"}
                    </Button>
                  )}
                  <div className="flex items-center gap-1 mt-2 flex-wrap">
                    <a href={`${BACKEND}${v.image_path}`} download target="_blank" rel="noreferrer" title="Download"><button className="text-[#9a9a9a] hover:text-white p-1"><DownloadSimple size={16} /></button></a>
                    <button data-testid={`v2-regen-${i}`} title="Regenerate this one" onClick={() => runBatch([v.concept])} disabled={busy} className="text-[#9a9a9a] hover:text-white p-1"><ArrowsClockwise size={16} /></button>
                    <button data-testid={`v2-approve-${i}`} title="Send to Approval" onClick={() => imageSendApproval({ client_id: clientId, image_path: v.image_path, title: `${v.concept} — ${(prompt || preset).slice(0, 40)}` }).then(() => toast.success("Sent to Approval Queue")).catch(() => toast.error("Failed"))} className="text-[#6B3DFF] hover:text-[#A56CFF] p-1"><PaperPlaneTilt size={16} /></button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid={`v2-campaign-menu-${i}`} className="ml-auto flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-white/15 text-[#cfcfcf] hover:border-[#6B3DFF]/50"><Megaphone size={13} className="brand-text" /> Campaign</button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#14141F] border-white/10 text-white">
                        <DropdownMenuLabel className="text-[#7a7a7a] text-xs">Generate from this image</DropdownMenuLabel>
                        {CAMPAIGN_ACTIONS.map((c) => (
                          <DropdownMenuItem key={c.a} data-testid={`v2-wf-${c.a}-${i}`} onClick={() => workflowMut.mutate({ image_path: v.image_path, action: c.a })} className="text-sm cursor-pointer focus:bg-white/10">{c.l}</DropdownMenuItem>
                        ))}
                        <DropdownMenuSeparator className="bg-white/10" />
                        <DropdownMenuItem data-testid={`v2-wf-approval-${i}`} onClick={() => workflowMut.mutate({ image_path: v.image_path, action: "send_approval" })} className="text-sm cursor-pointer focus:bg-white/10 text-[#6B3DFF]">Send image to Approval</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}
            {failedConcepts.map((f, i) => {
              const retrying = busy && regenLabel === f.concept;
              return (
                <div key={`fail-${f.concept}`} data-testid={`v2-variation-failed-${i}`}
                  className="card-matte p-3 aspect-square flex flex-col items-center justify-center gap-2 border border-red-500/25">
                  {retrying ? (
                    <>
                      <Sparkle size={26} className="brand-text ai-thinking" weight="fill" />
                      <span className="text-[11px] text-[#9a9a9a]">Retrying {f.concept}…</span>
                    </>
                  ) : (
                    <>
                      <Warning size={26} className="text-red-400" weight="fill" />
                      <span className="text-sm text-[#cfcfcf] font-semibold">Variation failed</span>
                      <span className="text-[10px] text-[#7a7a7a] text-center px-4">{f.concept}</span>
                      <Button data-testid={`v2-retry-${i}`} size="sm" onClick={() => runBatch([f.concept])} disabled={busy}
                        className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white mt-1">
                        <ArrowsClockwise size={13} className="mr-1" /> Retry
                      </Button>
                    </>
                  )}
                </div>
              );
            })}
            {busy && !regenLabel && Array.from({ length: Math.max(0, progress.total - variations.length) }).map((_, i) => (
              <div key={`ph-${i}`} className="card-matte p-3 aspect-square flex flex-col items-center justify-center gap-2" data-testid="v2-variation-loading">
                <Sparkle size={28} className="brand-text ai-thinking" weight="fill" />
                {i === 0 && (
                  <span className="text-[11px] text-[#9a9a9a] text-center px-3" data-testid="v2-progress-label">
                    {progress.current
                      ? `Variation ${Math.min(progress.done + 1, progress.total)} of ${progress.total} — ${progress.current}`
                      : "Starting…"}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <ImproveCompareDialog data={comparePass} onClose={() => setComparePass(null)} onKeepImproved={keepImproved} />
    </div>
  );
}

const KIT_FIELDS = [
  { k: "photography_style", l: "Photography Style", ph: "e.g. moody matte-black studio, high-end editorial" },
  { k: "preferred_lighting", l: "Preferred Lighting", ph: "e.g. warm golden-hour, dramatic side light, soft diffused" },
  { k: "preferred_backgrounds", l: "Preferred Backgrounds", ph: "e.g. black stone, reclaimed wood, marble counters" },
  { k: "tone_of_voice", l: "Marketing Tone of Voice", ph: "e.g. bold, confident, warm, premium" },
  { k: "brand_personality", l: "Brand Personality", ph: "e.g. rugged, artisanal, modern, energetic" },
  { k: "target_audience", l: "Target Audience", ph: "e.g. busy professionals & creators, 25-45, who love bold coffee" },
  { k: "cta_style", l: "CTA Style", ph: "e.g. punchy & direct — 'Fuel your grind', 'Shop the roast'" },
];

const COMPANY_FIELDS = [
  { k: "tagline", l: "Tagline", ph: "e.g. Your property, always perfect" },
  { k: "company_description", l: "Company Description", ph: "What the company does, in 1-2 sentences", area: true },
  { k: "service_catalog", l: "Services Offered", ph: "e.g. mowing, landscaping, snow removal, cleanup", area: true },
  { k: "service_areas", l: "Service Areas", ph: "e.g. Greater Cleveland, Akron, Canton" },
  { k: "phone", l: "Phone Number", ph: "e.g. (555) 123-4567" },
  { k: "email", l: "Email", ph: "e.g. hello@company.com" },
  { k: "cta_text", l: "Primary CTA", ph: "e.g. Get a Free Quote Today" },
  { k: "social_links", l: "Social Links", ph: "e.g. facebook.com/…, instagram.com/…" },
  { k: "marketing_style", l: "Preferred Marketing Style", ph: "e.g. bold & direct, community-focused, premium" },
  { k: "preferred_layouts", l: "Preferred Layouts", ph: "e.g. logo top-right, big hero image, minimal text" },
  { k: "brand_rules", l: "Brand Rules", ph: "e.g. never show competitors' equipment, always show uniforms", area: true },
];

export const CATEGORY_CHOICES = [
  { v: "coffee", l: "Coffee & Beverage", e: "☕" }, { v: "fitness", l: "Fitness & Health", e: "🏋️" },
  { v: "property_maintenance", l: "Property Maintenance", e: "🏡" }, { v: "property_preservation", l: "Property Preservation", e: "🏠" },
  { v: "ecommerce", l: "Ecommerce", e: "🛒" }, { v: "restaurant", l: "Restaurant", e: "🍕" },
  { v: "medical", l: "Medical", e: "🦷" }, { v: "automotive", l: "Automotive", e: "🚗" },
  { v: "other", l: "Other", e: "📦" },
];

const LOGO_PLACEMENTS = [
  { v: "auto", l: "Auto (smart)" }, { v: "top_left", l: "Top Left" }, { v: "top_right", l: "Top Right" },
  { v: "bottom_left", l: "Bottom Left" }, { v: "bottom_right", l: "Bottom Right" },
  { v: "center", l: "Center" }, { v: "watermark", l: "Watermark (subtle)" },
];

export function BrandKitPanel({ clientId, client }) {
  const qc = useQueryClient();
  const kitQ = useQuery({ queryKey: ["brand-kit", clientId], queryFn: () => getBrandKit(clientId), enabled: !!clientId });
  const [form, setForm] = useState(null);
  useEffect(() => { if (kitQ.data) setForm(kitQ.data); }, [kitQ.data]);
  const kit = form || kitQ.data || {};
  const save = useMutation({
    mutationFn: async () => {
      const kitPayload = { client_id: clientId, brand_lock: kit.brand_lock, logo_placement: kit.logo_placement, qr_mode: kit.qr_mode };
      [...KIT_FIELDS, ...COMPANY_FIELDS].forEach(({ k }) => { kitPayload[k] = kit[k] ?? ""; });
      await putBrandKit(kitPayload);
      if (kit.category && kit.category !== kitQ.data?.category) {
        await putBrandProfile({ client_id: clientId, category: kit.category });
      }
    },
    onSuccess: () => {
      toast.success("Master Brand Kit saved — every AI module now references it");
      qc.invalidateQueries({ queryKey: ["brand-kit", clientId] });
      qc.invalidateQueries({ queryKey: ["category-profile", clientId] });
    },
    onError: () => toast.error("Save failed"),
  });
  const set = (k, v) => setForm({ ...kit, [k]: v });
  const assets = kit.assets || {};
  const qrSrc = `${BACKEND}/api/brand/qr?client_id=${clientId}&t=${assets.qr ? "c" : "a"}`;

  return (
    <div className="grid lg:grid-cols-3 gap-5" data-testid="brand-kit-panel">
      <div className="lg:col-span-2 space-y-4">
        <div className="card-matte p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2"><LockKey size={18} className="brand-text" weight="fill" /><span className="font-head font-semibold">Brand Lock (production default)</span></div>
              <p className="text-xs text-[#7a7a7a] mt-1">When ON, AI keeps your products & branding exactly and only restyles the scene.</p>
            </div>
            <button data-testid="kit-brand-lock-toggle" onClick={() => set("brand_lock", !kit.brand_lock)}
              className={`relative h-7 w-12 rounded-full transition-colors ${kit.brand_lock ? "bg-[#6B3DFF]" : "bg-white/15"}`}>
              <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${kit.brand_lock ? "left-6" : "left-1"}`} />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div>
              <Label className="label-caps">Brand Category</Label>
              <Select value={kit.category || "other"} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="bg-[#10101A] border-white/10 mt-1" data-testid="kit-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#14141F] border-white/10 text-white max-h-64">
                  {CATEGORY_CHOICES.map((c) => <SelectItem key={c.v} value={c.v}>{c.e} {c.l}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-[#7a7a7a] mt-1">Drives the entire studio — product types, scenes, concepts & presets adapt to this business.</p>
            </div>
            <div>
              <Label className="label-caps">Logo Placement</Label>
              <Select value={kit.logo_placement || "auto"} onValueChange={(v) => set("logo_placement", v)}>
                <SelectTrigger className="bg-[#10101A] border-white/10 mt-1" data-testid="kit-logo-placement"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#14141F] border-white/10 text-white">
                  {LOGO_PLACEMENTS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-[#7a7a7a] mt-1">Where your REAL logo is composited on Brand Lock renders.</p>
            </div>
          </div>
        </div>

        <div className="card-matte p-5 space-y-3">
          <span className="font-head font-semibold text-sm">Company Profile · AI Memory</span>
          <p className="text-[11px] text-[#7a7a7a]">Vozelto remembers everything here — you'll never re-explain your company in a prompt again.</p>
          {COMPANY_FIELDS.map((f) => (
            <div key={f.k}>
              <Label className="label-caps">{f.l}</Label>
              {f.area
                ? <Textarea data-testid={`kit-${f.k}`} rows={2} value={kit[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} placeholder={f.ph} className="bg-[#10101A] border-white/10 mt-1" />
                : <Input data-testid={`kit-${f.k}`} value={kit[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} placeholder={f.ph} className="bg-[#10101A] border-white/10 mt-1" />}
            </div>
          ))}
        </div>

        <div className="card-matte p-5 space-y-3">
          <span className="font-head font-semibold text-sm">Visual & Voice DNA</span>
          {KIT_FIELDS.map((f) => (
            <div key={f.k}>
              <Label className="label-caps">{f.l}</Label>
              <Input data-testid={`kit-${f.k}`} value={kit[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} placeholder={f.ph} className="bg-[#10101A] border-white/10 mt-1" />
            </div>
          ))}
          <Button data-testid="kit-save-btn" onClick={() => save.mutate()} disabled={save.isPending} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white"><CheckCircle size={15} className="mr-1.5" weight="fill" /> {save.isPending ? "Saving…" : "Save Brand Kit"}</Button>
        </div>
      </div>

      <div className="space-y-4">
        <div className="card-matte p-5">
          <div className="flex items-center gap-2 mb-3"><Confetti size={16} className="brand-text" weight="fill" /><span className="font-head font-semibold text-sm">Official Assets · {kit.brand_name || client?.name}</span></div>
          <div className="space-y-2">
            {["logo", "alt_logo", "watermark", "packaging", "mug", "label", "qr"].map((k) => (
              <div key={k} data-testid={`kit-asset-${k}`} className="flex items-center justify-between rounded-lg border border-white/8 px-3 py-2">
                <span className="text-sm text-[#cfcfcf] capitalize">{k.replace("_", " ")}</span>
                {assets[k] ? <span className="flex items-center gap-1 text-xs text-emerald-400"><CheckCircle size={14} weight="fill" /> Uploaded</span> : <span className="text-xs text-amber-400">Missing</span>}
              </div>
            ))}
          </div>
          <p className="text-[11px] text-[#7a7a7a] mt-3">Upload official assets in the <b className="text-[#cfcfcf]">Create</b> tab → Brand Assets. They auto-apply on every render.</p>
        </div>
        <div className="card-matte p-5">
          <div className="flex items-center gap-2 mb-2"><QrCode size={16} className="brand-text" weight="fill" /><span className="font-head font-semibold text-sm">Brand QR Code</span></div>
          {kit.website || assets.qr ? (
            <div className="space-y-2">
              <img data-testid="kit-qr-preview" src={qrSrc} alt="Brand QR"
                className="h-32 w-32 rounded-lg border border-white/10 bg-white p-1"
                onError={(e) => { e.target.style.display = "none"; }} />
              <p className="text-[11px] text-[#7a7a7a]">
                {assets.qr ? "Using your custom uploaded QR code." : `Auto-generated from ${kit.website}. Upload a custom QR (Create tab → Brand Assets → QR) to override.`}
              </p>
            </div>
          ) : (
            <p className="text-[11px] text-[#7a7a7a]">Set the company website (Brand Manager) to auto-generate a QR code, or upload a custom one in the Create tab.</p>
          )}
        </div>
        <div className="card-matte p-5">
          <span className="font-head font-semibold text-sm">Brand Colors & Fonts</span>
          <div className="mt-2 space-y-2 text-xs">
            <div className="flex items-center justify-between"><span className="text-[#7a7a7a]">Accent</span><span className="flex items-center gap-1.5 text-[#cfcfcf]"><span className="h-4 w-4 rounded border border-white/20" style={{ background: kit.accent_color || "#6B3DFF" }} /> {kit.accent_color || "#6B3DFF"}</span></div>
            <div className="flex items-center justify-between"><span className="text-[#7a7a7a]">Secondary</span><span className="text-[#cfcfcf]">{kit.secondary_color || "—"}</span></div>
            <div className="flex items-center justify-between"><span className="text-[#7a7a7a]">Fonts</span><span className="text-[#cfcfcf]">{kit.fonts || "—"}</span></div>
            <div className="flex items-center justify-between"><span className="text-[#7a7a7a]">Voice</span><span className="text-[#cfcfcf] truncate max-w-[140px]">{kit.brand_voice || "—"}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
